import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddCiudadMutation } from "@/features/auxiliar/ciudades/api/add-ciudad";
import { DeleteCiudadMutation } from "@/features/auxiliar/ciudades/api/delete-ciudad";
import { EditCiudadMutation } from "@/features/auxiliar/ciudades/api/edit-ciudad";
import { getAllCiudades, useGetAllCiudadesQuery } from "@/features/auxiliar/ciudades/api/get-all-ciudades";
import { GetCiudadQuery } from "@/features/auxiliar/ciudades/api/get-ciudad";
import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";
import AddCiudadForm, { addCiudadFormSchema } from "@/features/auxiliar/ciudades/components/add-ciudad-form";
import EditCiudadForm, { editCiudadFormSchema } from "@/features/auxiliar/ciudades/components/edit-ciudad-form";

export default function CiudadesPage() {
    const openModal = useOpenModal();
    const hasWritePermiso = useHasPermisos(Permiso.WriteCiudadesAndBarrios);

    const filterData = useFilters({
        filters: [
            { type: "string", label: "Nombre", name: "nombre" },
            { type: "string", label: "Código", name: "codigo" },
            { type: "string", label: "Departamento", name: "departamentoId" },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllCiudadesQuery(filterData.filterData);

    const columnData = useColumns({
        columns: [
            { key: "nombre", header: "Nombre", render: row => formatString(row.nombre) },
            { key: "codigo", header: "Código", render: row => formatString(row.codigo) },
            { key: "departamentoNombre", header: "Departamento", render: row => formatString(row.departamentoNombre) },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ],
        key: "ciudades"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Ciudades">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}> 
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Drawer code="add">
            <ModalHeader>Agregar Ciudad</ModalHeader>
            <AddCiudadMutation>
                {mutation => <SchemaFormHook
                    schema={addCiudadFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify("La ciudad se ha agregado correctamente.");
                    }}
                    closeModalOnSubmit
                    submitOnce
                >
                    {form => <AddCiudadForm
                        form={form}
                        margin={3}
                    />}
                </SchemaFormHook>}
            </AddCiudadMutation>
        </Drawer>

        <Drawer code="edit">
            <ModalHeader>Editar Ciudad</ModalHeader>
            <ModalValueExtractor>
                {ciudadId => <GetCiudadQuery id={ciudadId}>
                    {query => <DataFetcher flex={1} query={query}>
                        {ciudad => <EditCiudadMutation id={ciudad.id}>
                            {mutation => <SchemaFormHook
                                schema={editCiudadFormSchema}
                                onSubmit={async ({ value }) => {
                                    await mutation.mutateAsync(value);
                                    notify("La ciudad se ha editado correctamente.");
                                }}
                                defaultValues={{ ...ciudad }}
                                closeModalOnSubmit
                                submitOnce
                            >
                                {form => <EditCiudadForm form={form} padding={3} />}
                            </SchemaFormHook>}
                        </EditCiudadMutation>}
                    </DataFetcher>}
                </GetCiudadQuery>}
            </ModalValueExtractor>
        </Drawer>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {ciudad => <DeleteCiudadMutation id={ciudad.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Borrar Ciudad "${ciudad.nombre}"`}
                        description={`¿Estás seguro que quieres borrar la ciudad "${ciudad.nombre}"?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`La ciudad "${ciudad.nombre}" se ha borrado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteCiudadMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCiudades}
            defaultFileTitle="Ciudades"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCiudades}
            defaultFileTitle="Ciudades"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCiudades}
            defaultFileTitle="Ciudades"
        />
    </ListPage>
}
